import { apiUrl } from "@/lib/api-base";
import { authFetch } from "@/lib/auth-fetch";

/**
 * Finishes the backend's browser-bound Google Calendar OAuth handshake after Google's
 * redirect lands back on /momentum or /settings carrying
 * `#calendar_completion=<secret>&calendar_provider=google` (or `#calendar_error=<code>&...`
 * on failure/decline). Converts either outcome into the `?calendar=connected|cancelled`
 * query-flag contract calendar-handoff.ts already reads — nothing downstream of this module
 * needs to know the handshake happened, or that it works differently from a plain redirect.
 *
 * Must run once per page load, before any other code rewrites the hash, and complete at
 * most once (mirrors auth-callback.tsx's guard for sign-in).
 */

const VERIFIER_KEY = "unfold_calendar_pkce_verifier";
let handled = false;

/** Called by calendar-api.ts right after /google/start returns a codeVerifier. */
export function saveCalendarCodeVerifier(codeVerifier: string): void {
  try {
    sessionStorage.setItem(VERIFIER_KEY, codeVerifier);
  } catch {
    // Private mode: completion will fail cleanly below (no matching verifier), not throw.
  }
}

function takeCalendarCodeVerifier(): string | null {
  try {
    const value = sessionStorage.getItem(VERIFIER_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
    return value;
  } catch {
    return null;
  }
}

function setCalendarResult(result: "connected" | "cancelled"): void {
  const url = new URL(window.location.href);
  url.searchParams.set("calendar", result);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}`);
}

export async function completeGoogleCalendarConnect(): Promise<void> {
  if (handled) return;
  const raw = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
  if (!raw) return;
  const params = new URLSearchParams(raw);
  const completionSecret = params.get("calendar_completion");
  const providerError = params.get("calendar_error");
  if (!completionSecret && !providerError) return;

  handled = true;
  // Strip the fragment immediately — it must never survive a refresh, reach analytics, or
  // get logged by anything else that reads the URL after this.
  window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);

  if (providerError) {
    setCalendarResult("cancelled");
    return;
  }

  const codeVerifier = takeCalendarCodeVerifier();
  if (!codeVerifier) {
    // Not the browser that started the connection (or private-mode storage failed) — this
    // secret can destroy the handshake but never complete it from here.
    await authFetch(apiUrl("/calendar/oauth/google/abandon"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ completionSecret }),
    }).catch(() => {});
    setCalendarResult("cancelled");
    return;
  }

  try {
    const res = await authFetch(apiUrl("/calendar/oauth/google/complete"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ completionSecret, codeVerifier }),
    });
    setCalendarResult(res.ok ? "connected" : "cancelled");
  } catch {
    setCalendarResult("cancelled");
  }
}
